/**
 * Mapa de pneus do veículo, visto de cima — no padrão da ilustração de
 * referência (docs/pneus/ilustracao.png): o chassi desenhado em traço cinza ao
 * fundo e cada pneu como um bloco escuro com a banda de rodagem, o número de
 * fogo na vertical e a borda na cor da condição. Rodado duplo mostra externo e
 * interno lado a lado, na ordem física (externo para fora). Cada pneu é um
 * botão: clicar abre o painel do pneu (ou "instalar aqui", se vazia).
 *
 * CAVALO 6x2 e CARRETAS de 3 e 4 eixos (a frota do cliente) usam as imagens de
 * referência de docs/pneus ("Painel de Status dos Pneus do Caminhão.png",
 * "Mapa Carreta 3 Eixos.png", "Mapa Carreta 4 Eixos.png") como fundo e põem
 * cada pneu exatamente sobre o pneu desenhado nela (coordenadas medidas na
 * imagem), com a textura do estepe da imagem, a borda na cor da condição e o
 * número de fogo REAL embaixo. Qualquer outra configuração usa o desenho em
 * traço (cavalo com cabine; carreta com pino-rei e pés de apoio).
 * As posições vêm da configuração do veículo — o desenho só as distribui.
 */
import type { TireMapAxleDto, TireMapPositionDto } from "@bi/types";
import { fmtMm } from "./tire-ui";
import fotoCavalo6x2 from "../../assets/pneus/cavalo-6x2.webp";
import fotoCarreta3 from "../../assets/pneus/carreta-3-eixos.webp";
import fotoCarreta4 from "../../assets/pneus/carreta-4-eixos.webp";
import fotoPneu from "../../assets/pneus/pneu.webp";

// ── Desenhos sobre as imagens de referência ─────────────────────────────────
// Cada imagem foi recortada do PNG original (só o chassi, sem a "Reserva") e as
// caixas dos pneus medidas pelas bordas coloridas, já no sistema do recorte.
// Eixos duplos: esq = [externo, interno]; dir = [interno, externo] (da esquerda
// para a direita na imagem).
interface Desenho {
  src: string;
  w: number; h: number;
  pneu: { w: number; h: number };
  frente: { y: number; esq: number; dir: number } | null;
  tras: Array<{ y: number; esq: [number, number]; dir: [number, number] }>;
}
const DESENHOS = {
  // PNG (140,10)-(800,1385)
  cavalo6x2: {
    src: fotoCavalo6x2, w: 660, h: 1375, pneu: { w: 97, h: 184 },
    frente: { y: 172, esq: 97, dir: 455 },
    tras: [{ y: 780, esq: [30, 130], dir: [435, 535] }, { y: 1030, esq: [29, 130], dir: [435, 535] }],
  },
  // PNG (70,20)-(870,1310)
  carreta3: {
    src: fotoCarreta3, w: 800, h: 1290, pneu: { w: 111, h: 213 }, frente: null,
    tras: [313, 601, 883].map((y) => ({ y, esq: [31, 145] as [number, number], dir: [540, 655] as [number, number] })),
  },
  // PNG (70,20)-(870,1310); rótulo "TP19/64" impresso na imagem foi apagado
  carreta4: {
    src: fotoCarreta4, w: 800, h: 1290, pneu: { w: 110, h: 206 }, frente: null,
    tras: [183, 459, 738, 1015].map((y) => ({ y, esq: [19, 132] as [number, number], dir: [556, 670] as [number, number] })),
  },
} satisfies Record<string, Desenho>;
const FOTO_LARGURA = 340;
/** folga (px da imagem) em volta da caixa medida, para cobrir o brilho impresso;
 * pequena na horizontal porque o rodado duplo tem só ~3 px entre os pneus */
const FOLGA = { x: 2, y: 7 };
// Geometria (px). Largura fixa: cabe no celular e mantém a proporção do desenho.
const W = 280;
const CX = W / 2;
const TW = 26;          // largura do pneu
const TH = 64;          // comprimento do pneu
const GAP = 3;          // entre externo e interno do rodado duplo
const MARGEM = 18;      // borda externa dos pneus
const PASSO = TH + 12;  // distância entre eixos agrupados
const TRILHO = 22;      // meia-distância entre as longarinas

const AXLE_TYPE: Record<TireMapAxleDto["axleType"], string> = {
  steering: "direcional",
  traction: "tração",
  free: "livre",
  lift: "auxiliar",
  spare: "estepe",
};

type Kind = "tractor" | "trailer";

interface Layout {
  height: number;
  axleY: Map<string, number>;
  rearStart: number;
}

function layout(eixos: TireMapAxleDto[], kind: Kind): Layout {
  const axleY = new Map<string, number>();
  const ordenados = [...eixos].sort((a, b) => a.order - b.order);
  if (kind === "tractor") {
    // direcionais na frente, sob a cabine; os demais agrupados na traseira
    const frente = ordenados.filter((a) => a.axleType === "steering");
    const tras = ordenados.filter((a) => a.axleType !== "steering");
    frente.forEach((a, i) => axleY.set(a.id, 62 + i * PASSO));
    const rearStart = Math.max(330, 62 + frente.length * PASSO + 170);
    tras.forEach((a, i) => axleY.set(a.id, rearStart + i * PASSO));
    const ultimo = Math.max(...axleY.values(), rearStart);
    return { height: ultimo + TH / 2 + 26, axleY, rearStart };
  }
  const rearStart = 250;
  ordenados.forEach((a, i) => axleY.set(a.id, rearStart + i * PASSO));
  const ultimo = Math.max(...axleY.values(), rearStart);
  return { height: ultimo + TH / 2 + 26, axleY, rearStart };
}

/** Lado ordenado de fora para dentro. */
function doLado(a: TireMapAxleDto, side: "left" | "right") {
  const rank = (p: TireMapPositionDto) => (p.slot === "inner" ? 1 : 0);
  return a.positions.filter((p) => p.side === side).sort((x, y) => rank(x) - rank(y) || x.order - y.order);
}

const BORDA: Record<string, string> = {
  critical: "border-danger-500",
  attention: "border-warning-400",
  normal: "border-tm-500",
};

function Pneu({ p, x, y, selected, onSelect }: {
  p: TireMapPositionDto; x: number; y: number; selected: boolean; onSelect: (p: TireMapPositionDto) => void;
}) {
  const t = p.tire;
  const detalhe = t
    ? [t.code, t.brand, t.treadMm != null ? fmtMm(t.treadMm) : null, t.life, t.pressurePsi != null ? `${Math.round(t.pressurePsi)} psi` : null]
        .filter(Boolean).join(" · ")
    : "vazia";
  return (
    <button
      type="button"
      onClick={() => onSelect(p)}
      title={`${p.label}: ${detalhe}`}
      aria-label={`${p.label}: ${t ? `pneu ${t.code}` : "vazia"}`}
      style={{ left: x, top: y - TH / 2, width: TW, height: TH }}
      className={`absolute rounded-md border-[0.1875rem] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500
        ${selected ? "ring-2 ring-tm-600 ring-offset-2" : ""}
        ${t ? `tire-tread hover:brightness-125 ${BORDA[t.condition ?? "normal"] ?? BORDA.normal}` : "border-dashed border-gray-300 bg-white/90 hover:border-tm-400"}`}
    >
      {t ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="[writing-mode:vertical-rl] rotate-180 text-[0.625rem] font-semibold tracking-wide text-white bg-black/45 rounded px-[0.0625rem] py-0.5 leading-none">
            {t.code}
          </span>
        </span>
      ) : (
        <span className="text-gray-400 text-sm leading-none">+</span>
      )}
    </button>
  );
}

function detalhePneu(p: TireMapPositionDto) {
  const t = p.tire;
  return t
    ? [t.code, t.brand, t.treadMm != null ? fmtMm(t.treadMm) : null, t.life, t.pressurePsi != null ? `${Math.round(t.pressurePsi)} psi` : null]
        .filter(Boolean).join(" · ")
    : "vazia";
}

const BRILHO: Record<string, string> = {
  critical: "0 0 8px rgba(239,68,68,.75)",
  attention: "0 0 8px rgba(250,204,21,.8)",
  normal: "0 0 8px rgba(34,197,94,.7)",
};
const BORDA_FOTO: Record<string, string> = {
  critical: "border-danger-500",
  attention: "border-yellow-400",
  normal: "border-green-500",
};

/** Pneu no padrão da imagem: textura real, borda/brilho da condição, nº de fogo embaixo. */
function PneuFoto({ p, x, y, w, h, selected, onSelect }: {
  p: TireMapPositionDto; x: number; y: number; w: number; h: number; selected: boolean; onSelect: (p: TireMapPositionDto) => void;
}) {
  const t = p.tire;
  const cond = t?.condition ?? "normal";
  // Seleção e brilho dividem o box-shadow: compor os dois (o ring do Tailwind sumiria).
  const sombra = [selected ? "0 0 0 2px #fff, 0 0 0 5px #2563eb" : null, t ? BRILHO[cond] ?? BRILHO.normal : null]
    .filter(Boolean).join(", ");
  return (
    <button
      type="button"
      onClick={() => onSelect(p)}
      title={`${p.label}: ${detalhePneu(p)}`}
      aria-label={`${p.label}: ${t ? `pneu ${t.code}` : "vazia"}`}
      style={{
        left: x, top: y, width: w, height: h,
        backgroundImage: `url(${fotoPneu})`, backgroundSize: "100% 100%",
        ...(sombra ? { boxShadow: sombra } : {}),
      }}
      className={`group absolute rounded-[0.5625rem] border-[0.1875rem] overflow-hidden bg-gray-900 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500
        ${t ? `hover:brightness-110 ${BORDA_FOTO[cond] ?? BORDA_FOTO.normal}` : "border-gray-300 hover:border-tm-400"}`}
    >
      {/* vazia: o mesmo pneu, apagado (na referência, "Vazio" é cinza) */}
      {!t && <span className="absolute inset-0 bg-slate-100/75 group-hover:bg-slate-100/55" aria-hidden />}
      {t && (
        <span
          className="absolute inset-x-[0.125rem] bottom-[0.125rem] rounded-b-[0.3125rem] bg-black/90 text-center font-bold leading-none text-white"
          style={{ fontSize: Math.max(11, Math.round(w * 0.27)), paddingTop: h * 0.035, paddingBottom: h * 0.035 }}
        >
          {t.code}
        </span>
      )}
    </button>
  );
}

/**
 * Encaixa a configuração numa das imagens: cavalo = 1 direcional simples + 2
 * eixos duplos; carreta = 3 ou 4 eixos duplos. Qualquer outra forma devolve
 * null (e o mapa usa o desenho em traço).
 */
function encaixeFoto(eixos: TireMapAxleDto[], kind: Kind) {
  const ord = [...eixos].sort((a, b) => a.order - b.order);
  const frente = ord.filter((a) => a.axleType === "steering");
  const tras = ord.filter((a) => a.axleType !== "steering");
  const desenho: Desenho | null =
    kind === "tractor" ? (frente.length === 1 && tras.length === 2 ? DESENHOS.cavalo6x2 : null)
    : frente.length === 0 && tras.length === 3 ? DESENHOS.carreta3
    : frente.length === 0 && tras.length === 4 ? DESENHOS.carreta4
    : null;
  if (!desenho) return null;
  if (ord.some((a) => a.positions.some((p) => p.side === "center"))) return null;

  const caixas: Array<{ p: TireMapPositionDto; x: number; y: number }> = [];
  if (desenho.frente) {
    const f = frente[0]!;
    const fe = doLado(f, "left"); const fd = doLado(f, "right");
    if (fe.length !== 1 || fd.length !== 1) return null;
    caixas.push({ p: fe[0]!, x: desenho.frente.esq, y: desenho.frente.y }, { p: fd[0]!, x: desenho.frente.dir, y: desenho.frente.y });
  }
  for (const [i, a] of tras.entries()) {
    const esq = doLado(a, "left"); const dir = doLado(a, "right");
    if (esq.length !== 2 || dir.length !== 2) return null;
    const g = desenho.tras[i]!;
    // doLado devolve de fora para dentro dos dois lados
    caixas.push({ p: esq[0]!, x: g.esq[0], y: g.y }, { p: esq[1]!, x: g.esq[1], y: g.y });
    caixas.push({ p: dir[1]!, x: g.dir[0], y: g.y }, { p: dir[0]!, x: g.dir[1], y: g.y });
  }
  return { desenho, caixas };
}

function MapaFoto({ encaixe, selectedId, onSelect }: {
  encaixe: NonNullable<ReturnType<typeof encaixeFoto>>; selectedId?: string | null; onSelect: (p: TireMapPositionDto) => void;
}) {
  const { desenho: d, caixas } = encaixe;
  const k = FOTO_LARGURA / d.w;
  return (
    <div className="relative" style={{ width: FOTO_LARGURA, height: d.h * k }}>
      <img src={d.src} alt="" aria-hidden className="absolute inset-0 w-full h-full select-none pointer-events-none" draggable={false} />
      {/* Cada pneu cobre a caixa medida + uma folga: a borda colorida impressa na
          imagem tem brilho em volta, que vazaria ao redor de um pneu vazio. */}
      {caixas.map(({ p, x, y }) => (
        <PneuFoto key={p.id} p={p} x={(x - FOLGA.x) * k} y={(y - FOLGA.y) * k} w={(d.pneu.w + 2 * FOLGA.x) * k} h={(d.pneu.h + 2 * FOLGA.y) * k} selected={selectedId === p.id} onSelect={onSelect} />
      ))}
    </div>
  );
}

const LEGENDA_FOTO: Array<{ cor: string; nome: string }> = [
  { cor: "#22c55e", nome: "OK" },
  { cor: "#facc15", nome: "Atenção" },
  { cor: "#ef4444", nome: "Crítico" },
  { cor: "#e2e8f0", nome: "Vazio" },
];

/**
 * O painel inteiro no layout da imagem de referência: o chassi com os pneus,
 * o quadro "Reserva" e a barra de legenda, na mesma moldura clara.
 */
function PainelFoto({ foto, estepes, selectedId, onSelect }: {
  foto: NonNullable<ReturnType<typeof encaixeFoto>>;
  estepes: TireMapAxleDto | undefined;
  selectedId?: string | null;
  onSelect: (p: TireMapPositionDto) => void;
}) {
  const k = FOTO_LARGURA / foto.desenho.w;
  const pw = foto.desenho.pneu.w * k;
  const ph = foto.desenho.pneu.h * k;
  return (
    <div className="overflow-x-auto">
      <div className="mx-auto w-fit rounded-2xl border border-slate-200 p-3 shadow-sm" style={{ background: "rgb(249,252,253)" }}>
        <MapaFoto encaixe={foto} selectedId={selectedId} onSelect={onSelect} />
        {estepes && estepes.positions.length > 0 && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-white/60 px-3 pb-3 pt-2">
            <p className="text-lg font-bold text-slate-800 mb-2">Reserva</p>
            <div className="relative" style={{ height: ph }}>
              {estepes.positions.map((p, i) => (
                <PneuFoto key={p.id} p={p} x={i * (pw + 12)} y={0} w={pw} h={ph} selected={selectedId === p.id} onSelect={onSelect} />
              ))}
            </div>
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center justify-around gap-x-3 gap-y-1 rounded-xl border border-slate-200 bg-white/60 px-3 py-2 text-xs text-slate-700">
          {LEGENDA_FOTO.map((l) => (
            <span key={l.nome} className="inline-flex items-center gap-1.5">
              <span className="h-3.5 w-3.5 rounded-[0.25rem] border border-black/10" style={{ background: l.cor }} />
              {l.nome}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Traço do chassi ao fundo. Só decoração: o que importa são as posições. */
function Chassi({ kind, height, eixos, axleY, rearStart }: {
  kind: Kind; height: number; eixos: TireMapAxleDto[]; axleY: Map<string, number>; rearStart: number;
}) {
  const traco = { stroke: "currentColor", strokeWidth: 1.4, fill: "none" } as const;
  const travessas: number[] = [];
  for (let y = kind === "tractor" ? 130 : 90; y < height - 30; y += 58) travessas.push(y);
  const ys = eixos.map((a) => axleY.get(a.id)!).filter((y) => y != null);

  return (
    <svg className="absolute inset-0 text-gray-300" width={W} height={height} viewBox={`0 0 ${W} ${height}`} aria-hidden>
      {kind === "tractor" ? (
        <>
          {/* cabine */}
          <rect x={8} y={4} width={W - 16} height={106} rx={18} {...traco} />
          <path d={`M26 20 Q${CX} 8 ${W - 26} 20`} {...traco} />
          {/* bloco do motor entre as longarinas */}
          <rect x={CX - 34} y={36} width={68} height={58} rx={8} {...traco} />
          {/* longarinas */}
          <line x1={CX - TRILHO} y1={110} x2={CX - TRILHO} y2={height - 12} {...traco} />
          <line x1={CX + TRILHO} y1={110} x2={CX + TRILHO} y2={height - 12} {...traco} />
          {/* tanques de combustível e de ar, caixa de bateria */}
          <rect x={46} y={150} width={58} height={74} rx={10} {...traco} />
          <rect x={W - 104} y={150} width={58} height={74} rx={10} {...traco} />
          <rect x={58} y={238} width={40} height={22} rx={6} {...traco} />
          <rect x={W - 98} y={238} width={40} height={22} rx={6} {...traco} />
          <rect x={W - 98} y={118} width={40} height={24} rx={4} {...traco} />
          {/* cardã até o diferencial */}
          <line x1={CX} y1={112} x2={CX} y2={rearStart - 8} {...traco} />
          {[150, rearStart - 40].map((y) => <circle key={y} cx={CX} cy={y} r={4} {...traco} />)}
          {eixos.filter((a) => a.axleType === "traction").map((a) => (
            <rect key={a.id} x={CX - 12} y={axleY.get(a.id)! - 10} width={24} height={20} rx={5} {...traco} />
          ))}
          {/* quinta roda sobre o conjunto traseiro */}
          {ys.length > 1 && (() => {
            const traseiros = eixos.filter((a) => a.axleType !== "steering").map((a) => axleY.get(a.id)!);
            if (traseiros.length === 0) return null;
            const cy = (Math.min(...traseiros) + Math.max(...traseiros)) / 2;
            return <circle cx={CX} cy={cy} r={30} {...traco} strokeDasharray="4 3" />;
          })()}
        </>
      ) : (
        <>
          {/* contorno do semirreboque, pino-rei e pés de apoio */}
          <rect x={30} y={6} width={W - 60} height={height - 18} rx={6} {...traco} strokeDasharray="5 4" />
          <rect x={CX - 46} y={14} width={92} height={56} rx={6} {...traco} />
          <circle cx={CX} cy={42} r={7} {...traco} />
          <line x1={CX - TRILHO} y1={70} x2={CX - TRILHO} y2={height - 14} {...traco} />
          <line x1={CX + TRILHO} y1={70} x2={CX + TRILHO} y2={height - 14} {...traco} />
          <rect x={CX - TRILHO - 34} y={140} width={18} height={18} rx={3} {...traco} />
          <rect x={CX + TRILHO + 16} y={140} width={18} height={18} rx={3} {...traco} />
          <line x1={CX - TRILHO - 16} y1={149} x2={CX + TRILHO + 16} y2={149} {...traco} />
        </>
      )}
      {travessas.map((y) => (
        <line key={y} x1={CX - TRILHO} y1={y} x2={CX + TRILHO} y2={y} {...traco} />
      ))}
    </svg>
  );
}

export function TireMap({ axles, selectedId, onSelect, kind = "tractor" }: {
  axles: TireMapAxleDto[];
  selectedId?: string | null;
  onSelect: (p: TireMapPositionDto) => void;
  /** cavalo (padrão, 6x2) ou carreta — muda a silhueta do chassi */
  kind?: Kind;
}) {
  const eixos = axles.filter((a) => a.axleType !== "spare");
  const estepes = axles.find((a) => a.axleType === "spare");
  const { height, axleY, rearStart } = layout(eixos, kind);
  const foto = encaixeFoto(eixos, kind);

  if (foto) return <PainelFoto foto={foto} estepes={estepes} selectedId={selectedId} onSelect={onSelect} />;

  return (
    <div className="overflow-x-auto">
      <div className="mx-auto w-fit py-2">
        <p className="text-center text-[0.6875rem] font-semibold uppercase tracking-widest text-gray-400 mb-1">Frente</p>
        {(
        <div className="relative" style={{ width: W, height }}>
          <Chassi kind={kind} height={height} eixos={eixos} axleY={axleY} rearStart={rearStart} />
          {eixos.map((a) => {
            const y = axleY.get(a.id)!;
            const esq = doLado(a, "left");
            const dir = doLado(a, "right");
            const centro = a.positions.filter((p) => p.side === "center");
            const fimEsq = MARGEM + esq.length * (TW + GAP) - GAP;
            const inicioDir = W - MARGEM - dir.length * (TW + GAP) + GAP;
            return (
              <div key={a.id}>
                {/* eixo */}
                <div
                  className="absolute h-[0.1875rem] rounded bg-gray-400"
                  style={{ left: fimEsq, width: Math.max(0, inicioDir - fimEsq), top: y - 1.5 }}
                  title={`${a.code} · ${AXLE_TYPE[a.axleType]}`}
                  aria-hidden
                />
                {esq.map((p, k) => (
                  <Pneu key={p.id} p={p} x={MARGEM + k * (TW + GAP)} y={y} selected={selectedId === p.id} onSelect={onSelect} />
                ))}
                {dir.map((p, k) => (
                  <Pneu key={p.id} p={p} x={W - MARGEM - TW - k * (TW + GAP)} y={y} selected={selectedId === p.id} onSelect={onSelect} />
                ))}
                {centro.map((p, k) => (
                  <Pneu key={p.id} p={p} x={CX - TW / 2 + k * (TW + GAP)} y={y} selected={selectedId === p.id} onSelect={onSelect} />
                ))}
              </div>
            );
          })}
        </div>
        )}

        {estepes && estepes.positions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-dashed border-surface-border">
            <p className="text-sm text-gray-700 mb-2">Reserva</p>
            <div className="relative flex gap-1.5" style={{ height: TH }}>
              {estepes.positions.map((p, k) => (
                <Pneu key={p.id} p={p} x={k * (TW + 6)} y={TH / 2} selected={selectedId === p.id} onSelect={onSelect} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[0.6875rem] text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm border-2 border-green-500 bg-gray-800" /> Normal</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm border-2 border-yellow-400 bg-gray-800" /> Atenção</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm border-2 border-danger-500 bg-gray-800" /> Crítico</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm border-2 border-dashed border-gray-300 bg-white" /> Vazia</span>
        </div>
      </div>
    </div>
  );
}
